import { RcFile } from "antd/es/upload";

import { t } from "@lingui/macro";
import { message } from "antd";
import { Dictionary, isArray, sortBy } from "lodash";
import { v4 } from "uuid";

import { FIELD_TYPES } from "components/forms-view/questions";

import { Form, FormQuestion } from "types";

import { formatDate } from "utils/datetime-utils";
import { IHttpClient } from "utils/http-client-utils";
import { sleep } from "utils/uuid-utils";

export const getFormStateColor = (state: Form["state"]) => {
  switch (state) {
    case "active":
      return "green";
    case "archived":
      return "yellow";
    case "draft":
      return "grey";
    default:
      return "grey";
  }
};

export type FormAction = "publish_after_create" | "publish" | "archive";

export type DraggableQuestion = {
  key: string; // question id or generated on question creation uuid
  id?: string;
  question: FormQuestion["question"];
  sort_order: number;
  type: FormQuestion["question_type"];
  required?: FormQuestion["required"];
  description?: FormQuestion["description"];
  additional_field?: FormQuestion["additional_field"];
  choices?: { id: string; value: string }[];
  active?: FormQuestion["active"];
  autofill?: FormQuestion["autofill"];
};

export const convertIntoFormQuestion: (input: DraggableQuestion[], formUri: string) => any[] = (input, formUri) => {
  return input.map((x, i) => ({
    id: x.id,
    active: x.active,
    form: formUri,
    sort_order: i,
    question: x.question,
    required: !!x?.required,
    description: x.description,
    question_type: x.type,
    ...(x.choices && { choices: x.choices?.map((x) => x.value) }),
    additional_field: x.additional_field,
    autofill: x.autofill,
  }));
};

export const convertIntoDraggableQuestion: (input: FormQuestion[]) => DraggableQuestion[] = (input) => {
  return sortBy(input, ["sort_order", "id"]).map((x, i) => ({
    id: x.id,
    key: x.id || v4(),
    question: x.question,
    sort_order: x.sort_order as unknown as number,
    choices: x.choices?.map((x) => ({ id: v4(), value: x })),
    type: x.question_type,
    description: x.description,
    required: x.required,
    additional_field: x.additional_field,
    active: x.active,
    autofill: x.autofill,
  }));
};

export const parseAnswers = (
  answers: { [key: string]: any },
  questionsBySlug: Dictionary<FormQuestion>,
  fileUploadMapping: { [key: string]: string }
) => {
  return Object.keys(answers).reduce(
    (result, slug: string) => {
      const answer = answers[slug];
      const question = questionsBySlug[slug];

      if (question.question_type === "attachment" && answer && isArray(answer) && question.url) {
        const fileUploads = answer.map((a: RcFile) => fileUploadMapping[a.uid]);

        result.postableAnswers[slug] = fileUploads.toString();

        // append file upload to non attachment array
        fileUploads.forEach((fileUplodUri) => {
          result.attachmentAnswers.push({ fileUpload: fileUplodUri, question: question.url as string });
        });
      } else if (question.question_type === "driver" || question.question_type === "vehicle") {
        const key = question.question_type === "driver" ? "driver" : "vehicle";

        result[key] = answer;
        result.postableAnswers[slug] = answer;
      } else {
        result.postableAnswers[slug] = answer;
      }

      return result;
    },
    { attachmentAnswers: [], postableAnswers: {}, vehicle: null, driver: null } as {
      attachmentAnswers: { fileUpload: string; question: string }[];
      postableAnswers: { [key: string]: string };
      vehicle: null | string;
      driver: null | string;
    }
  );
};

export const getQuestionFieldOptions: (questions: DraggableQuestion[]) => any[] = (questions) => {
  const hasVehicleQuestion = questions.find((q) => q.type === "vehicle");
  const hasDriverQuestion = questions.find((q) => q.type === "driver");

  return FIELD_TYPES.map((field: any) => ({
    label: field.name(),
    key: field.key,
    icon: field.icon,
    disabled: !!((field.key === "vehicle" && hasVehicleQuestion) || (field.key === "driver" && hasDriverQuestion)),
  }));
};

export const getResponseAnswerValue = ({
  fileList,
  questionType,
  rawValue,
  driver,
  vehicle,
}: {
  questionType: FormQuestion["question_type"];
  rawValue: string | string[] | undefined;
  fileList?: string[];
  driver?: string;
  vehicle?: string;
}) => {
  switch (questionType) {
    case "time":
    case "date":
      return formatDate(rawValue as string);
    case "attachment":
      return fileList || [];
    case "driver":
      return driver || "-";
    case "vehicle":
      return vehicle || "-";
    default:
      return rawValue || "-";
  }
};

export const getFormResponseExport = async (
  id: string,
  language: string | undefined,
  httpClient: IHttpClient,
  format: 'pdf' | 'xlsx' = 'pdf'
) => {
  try {
    message.loading(t`Requesting file from server`);
    const response = await httpClient.post(`form_responses/${id}/export/`, { data: { format, language } });

    let state = response.state;
    let result = null;

    while (state === "pending" || state === "in_progress") {
      await sleep(2000);
      const exportResult = await httpClient.get(response.url);
      state = exportResult.state;
      result = exportResult;
    }

    message.destroy();
    if (result.content) {
      message.success(t`Download URL recieved`);
      return result.content;
    }

    return result.content;
  } catch (error) {
    message.destroy();
    message.error(t`Failed to download file`);
    return undefined;
  }
};

const UNIQUE_FIELDS = ["driver", "vehicle"];

export const canDuplicate = (type: FormQuestion["question_type"], questions: DraggableQuestion[]) => {
  if (UNIQUE_FIELDS.includes(type)) {
    return !questions.find((q) => q.type === type);
  }

  return true;
};
